import { PageTransition } from '@/constant/transition'
import { container } from '@/style/container.css'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from 'react-bootstrap/Button'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { AddressSchema, fetchUserFirstAddr, PREFECTURE_TO_ENUM } from './data'
import type { AddressForm, ZipCloudResponse } from './data'
import { useMutation, useQuery } from '@tanstack/react-query'
import { fetchAreaPrefecturesMap, type AreaPrefectureMapping } from '@/util/ref'
import { useTranslation } from 'react-i18next'
import * as http from '@/util/http'
import { ProgressFlags } from '@/util/progress'
import { syncProgress } from '../helper'
import { queryClient } from '@/util/queryClient'
import { useEffect } from 'react'

export default function UserAddress() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: userAddr } = useQuery<AddressForm>({
    queryKey: ['current-user-address'],
    queryFn: fetchUserFirstAddr,
    staleTime: 1000 * 60 * 60,
  })
  const {
    data: areaPrefectureMapping,
    isLoading: areaPrefectureLoading,
    isError: areaPrefectureError,
  } = useQuery<AreaPrefectureMapping>({
    queryKey: ['area-prefecture'],
    queryFn: fetchAreaPrefecturesMap,
    staleTime: Infinity,
  })

  const {
    register,
    setValue,
    watch,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(AddressSchema),
    mode: 'onChange',
    values: userAddr,
  })

  const addrMutation = useMutation({
    mutationFn: (payload: object) => http.put('/api/v1/user/addr', { payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['current-user-address'] })
      syncProgress(ProgressFlags.ADDR_FILLED)
      navigate('/onboarding/program-type-selection', {
        state: { direction: PageTransition.FromRight },
      })
    },
    onError: (err) => console.log(err),
  })

  const [region] = watch(['subnationalUnit'])

  async function lookupAddressByZip(zipcode: string) {
    if (zipcode.length === 7) {
      const { data } = await http.get<ZipCloudResponse>(
        `https://zipcloud.ibsnet.co.jp/api/search`,
        {
          credentials: 'same-origin',
          queryParams: { zipcode },
          forceJson: true,
        }
      )
      if (data.results?.length) {
        const prefecture = PREFECTURE_TO_ENUM[data.results[0].address1] || ''
        setValue('subnationalUnit', areaPrefectureMapping?.prefectureToArea?.[prefecture] ?? '')
        setValue('adminDivision', prefecture)
        setValue('city', data.results[0].address2)
        setValue('district', data.results[0].address3)
      }
    }
  }

  useEffect(() => {
    console.log(userAddr)
    reset({ ...userAddr })
  }, [reset, userAddr])

  return (
    <div className={container}>
      <h1>住所</h1>
      <FloatingLabel controlId="input-postal-code" label="郵便番号" className="mb-3">
        <Form.Control
          {...register('postalCode')}
          type="text"
          isInvalid={!!errors.postalCode}
          placeholder="郵便番号"
          onBlur={async (e) => {
            await lookupAddressByZip(e.target.value.replace('-', ''))
          }}
          onKeyDown={async (e) => {
            const target = e.target as HTMLInputElement
            await lookupAddressByZip(target.value.replace('-', ''))
          }}
        />
      </FloatingLabel>
      <FloatingLabel controlId="area-selector" label="地方" className="mb-3">
        <Form.Select
          {...register('subnationalUnit')}
          aria-label="Floating label area selector"
          onChange={(e) => {
            register('subnationalUnit').onChange(e)
            setValue('adminDivision', '')
          }}
        >
          <option value="" disabled hidden>
            地方を選択してください
          </option>
          {areaPrefectureError ? (
            <option>error</option>
          ) : areaPrefectureLoading || !areaPrefectureMapping ? (
            <option>Loading...</option>
          ) : (
            Object.keys(areaPrefectureMapping.areaToPrefecture).map((k) => (
              <option key={k} value={k}>
                {t(`region.${k}`)}
              </option>
            ))
          )}
        </Form.Select>
      </FloatingLabel>
      <FloatingLabel controlId="prefecture-selector" label="都道府県" className="mb-3">
        <Form.Select
          {...register('adminDivision')}
          aria-label="Floating label prefecture selector"
          onChange={(e) => {
            register('adminDivision').onChange(e)
            const area = areaPrefectureMapping?.prefectureToArea?.[e.target.value]
            if (area) setValue('subnationalUnit', area)
          }}
        >
          <option value="" disabled hidden>
            都道府県を選択してください
          </option>
          {areaPrefectureError ? (
            <option>error</option>
          ) : areaPrefectureLoading ? (
            <option>Loading...</option>
          ) : (
            (
              areaPrefectureMapping?.areaToPrefecture?.[region] ??
              Object.values(areaPrefectureMapping?.areaToPrefecture ?? {}).flat()
            ).map((k) => (
              <option key={k} value={k}>
                {t(`prefecture.${k}`)}
              </option>
            ))
          )}
        </Form.Select>
      </FloatingLabel>
      <FloatingLabel controlId="input-city" label="市区町村" className="mb-3">
        <Form.Control
          {...register('city')}
          type="text"
          placeholder="市区町村"
          isInvalid={!!errors.city}
        />
      </FloatingLabel>
      <FloatingLabel controlId="input-district" label="町域・字（例：丸の内）" className="mb-3">
        <Form.Control
          {...register('district')}
          type="text"
          placeholder="例：丸の内"
          isInvalid={!!errors.district}
        />
      </FloatingLabel>
      <FloatingLabel
        controlId="input-addr-line-1"
        label="丁目・番地まで入力（例：1-1-1）"
        className="mb-3"
      >
        <Form.Control
          {...register('addressLine1')}
          type="text"
          placeholder="例：1-1-1"
          isInvalid={!!errors.addressLine1}
        />
      </FloatingLabel>
      <FloatingLabel
        controlId="input-addr-line-2"
        label="ビル名・部屋番号など（任意）"
        className="mb-3"
      >
        <Form.Control
          {...register('addressLine2')}
          type="text"
          placeholder="例：ビル名・部屋番号など（任意）"
          isInvalid={!!errors.addressLine2}
        />
      </FloatingLabel>
      <Stack direction="horizontal">
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={() => {
            navigate('/onboarding/personal-info', {
              state: { direction: PageTransition.FromLeft },
            })
          }}
        >
          ←
        </Button>
        <Button
          variant="outline-primary"
          size="lg"
          className="ms-auto"
          onClick={handleSubmit(
            (data) => addrMutation.mutate([data]),
            (err) => console.log(err)
          )}
        >
          →
        </Button>
      </Stack>
    </div>
  )
}
